import { produce } from 'immer';
import _ from 'lodash';
import { Dispatch, SetStateAction, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { cardTypeDescriptionMapping } from '../..';
import {
  formatPrice,
  getErrorMessage,
  hasValidationErrors,
  setValidationErrors,
  showErrorToast,
} from '../../../../../../../common/global/helpers';
import useMount from '../../../../../../../common/hooks/useMount';
import Button from '../../../../../../../common/widgets/Button';
import Dialog from '../../../../../../../common/widgets/Dialog';
import Icons from '../../../../../../../common/widgets/Icons';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../../../../common/widgets/Notification';
import { config } from '../../../../../config';
import { CustomerBudgetContext } from '../../../../../contexts/CustomerBudgetContext';
import { CardFormFactor } from '../../../../../enums';
import { rewriteImageUrl } from '../../../../../helpers';
import { CustomerAddressModel } from '../../../../../models/Cards';
import {
  ErrorState,
  ErrorType,
  ValidationErrorModel,
} from '../../../../../models/Errors';
import { resources } from '../../../../../resources/resources';
import { cardService } from '../../../../../services/card';
import { LimitErrorDetailsDialog } from '../../../../Common/Dialogs/LimitErrorDetailsDialog';
import { SelectedCard } from '../common';

interface SummaryProps {
  cardFormFactor: CardFormFactor;
  selectedCard: SelectedCard;
  customerAddressModel?: CustomerAddressModel;
  setCustomerAddressModel: (data: CustomerAddressModel) => void;
  errors: ErrorState;
  setErrors: Dispatch<SetStateAction<ErrorState>>;
}

interface SummaryState {
  isSaveButtonLoading?: boolean;
  showSuccessDialog?: boolean;
  showErrorPanel?: boolean;
}

export const Summary = (props: SummaryProps) => {
  const {
    cardFormFactor,
    selectedCard,
    customerAddressModel,
    setCustomerAddressModel,
    errors,
    setErrors,
  } = props;
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<SummaryState>({});
  const [showLimitDialog, setShowLimitDialog] = useState<boolean>(false);
  const isSubmittingRef = useRef(false);
  const { customerBudget, setCustomerBudget } = useContext(
    CustomerBudgetContext,
  );

  const loadAddress = async () => {
    const resp = await cardService.getCustomerAddress();
    if (!resp.hasErrors) {
      setCustomerAddressModel(resp.data);
    } else {
      if (resp.errorType === ErrorType.ValidationError) {
        const validationError = resp.error as ValidationErrorModel;
        const invalidAddressError = validationError.details.find(
          e => e.payload?.invalidAddress,
        );
        if (invalidAddressError) {
          showErrorToast(getErrorMessage(invalidAddressError, translate));
        }
      }
      setState({ showErrorPanel: true });
    }
  };

  const orderCard = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setState(
      produce(d => {
        d.isSaveButtonLoading = true;
      }),
    );
    try {
      const resp = await cardService.buyCard({
        productId: selectedCard.productId!,
        options: selectedCard.serializedOptions!,
      });
      setState(
        produce(d => {
          d.isSaveButtonLoading = false;
        }),
      );

      if (!resp.hasErrors) {
        setState(
          produce(d => {
            d.showSuccessDialog = true;
          }),
        );
        setCustomerBudget(
          produce(customerBudget, d => {
            d.benefitBudgetBalance -= selectedCard.price!.price;
          }),
        );
      } else {
        if (resp.errorType === ErrorType.ValidationError) {
          setValidationErrors(resp, setErrors);
        } else {
          showErrorToast(translate(resources.errors.generic));
        }
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useMount(() => {
    if (!customerAddressModel && cardFormFactor === CardFormFactor.Plastic) {
      loadAddress();
    }
  });

  const cardTypeName = translate(
    _.get(resources, cardTypeDescriptionMapping[selectedCard.type]),
  );

  return (
    <Loader
      loading={
        cardFormFactor === CardFormFactor.Plastic && !customerAddressModel
      }
      loader={<LoadingSpinner />}
      showErrorPanel={state.showErrorPanel}
    >
      <h1 className="card-step-title">
        {translate(resources.employeeCards.buy.step4.title)}
      </h1>
      {hasValidationErrors(errors, 'null') && (
        <Notification
          type={NotificationType.Error}
          messages={errors.null.map(e => ({
            message: getErrorMessage(e, translate),
            onShowDetailsClick: e.payload?.isAutobudgetLimitError
              ? () => setShowLimitDialog(true)
              : undefined,
          }))}
        />
      )}
      <div className="card-summary-section">
        <div className="cs-wrapper">
          {/* Selected card section */}
          <div className="card-details-small">
            <div className="cds-image">
              <div>
                <img
                  src={rewriteImageUrl(selectedCard.thumbnail)}
                  alt={cardTypeName}
                />
              </div>
            </div>
            <div className="cds-details">
              <div className="cds-name">{cardTypeName}</div>
              <div className="cds-availability">
                <span className="av-nm">
                  {translate(resources.employeeCards.topUp.availableNextMonth)}
                </span>
              </div>
              <div className="cds-availability-info">
                {translate(resources.employeeCards.buy.step4.schedule.full)}
              </div>
            </div>
          </div>
          {/* Summary info panels */}
          <div className="cs-info">
            {cardFormFactor === CardFormFactor.Plastic && (
              <div className="cs-info-panel">
                <div className="cs-info-icon">
                  <div>
                    <Icons.CardAddress />
                  </div>
                </div>
                <div className="cs-info-details">
                  <div className="cs-info-title">
                    {translate(resources.employeeCards.buy.step4.address)}
                  </div>
                  <div>{customerAddressModel?.companyName}</div>
                  <div>{customerAddressModel?.companyAddress}</div>
                </div>
              </div>
            )}
            {cardFormFactor === CardFormFactor.Virtual && (
              <div className="cs-info-panel">
                <div className="cs-info-icon">
                  <div>
                    <Icons.CardAddress />
                  </div>
                </div>
                <div className="cs-info-details">
                  <div>
                    {translate(
                      resources.employeeCards.buy.step4.virtualCardInfo,
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="cs-info-panel">
              <div className="cs-info-icon">
                <div>
                  <Icons.CardDetails />
                </div>
              </div>
              <div className="cs-info-details">
                <div className="cs-info-title">
                  {translate(resources.employeeCards.buy.step4.details, {
                    0: cardTypeName,
                  })}
                </div>
                <div>
                  <b>{translate(resources.employeeCards.buy.step4.sum)}</b>{' '}
                  {formatPrice(selectedCard.price!.netPrice)} {config.currency}
                </div>
                <div>
                  <b>{translate(resources.employeeCards.buy.step4.taxes)}</b>{' '}
                  {formatPrice(selectedCard.price!.employeeTax)}{' '}
                  {config.currency}
                </div>
                <div>
                  <b>
                    {translate(resources.employeeCards.buy.step4.available)}{' '}
                  </b>
                  {translate(resources.employeeCards.buy.step4.schedule.short)}
                </div>
              </div>
            </div>
          </div>
          {/* Summary action */}
          <div className="cs-action">
            <Button
              onClick={orderCard}
              loading={state.isSaveButtonLoading}
              className="btn btn-solid btn-lg btn-full"
            >
              {translate(resources.placeOrder)}
            </Button>
          </div>
          {state.showSuccessDialog && (
            <SuccessDialog
              cardFormFactor={cardFormFactor}
              mainImage={selectedCard.mainImage}
              onClose={() => navigate('/my-account/cards')}
            />
          )}
        </div>
      </div>
      {showLimitDialog && (
        <LimitErrorDetailsDialog
          closeDialog={() => setShowLimitDialog(false)}
        />
      )}
    </Loader>
  );
};

interface SuccessDialogProps {
  mainImage?: string;
  cardFormFactor: CardFormFactor;
  onClose: () => void;
}

const SuccessDialog = (props: SuccessDialogProps) => {
  const { mainImage, cardFormFactor, onClose } = props;
  const { t: translate } = useTranslation();
  return (
    <Dialog
      closeOnClickOutside
      showCloseIcon
      closeDialog={onClose}
      className="dialog-s topup-success-dialog"
    >
      <div className="dialog-content">
        <div className="dialog-image">
          <img
            src={rewriteImageUrl(mainImage)}
            alt={translate(resources.employeeCards.buy.step4.dialog.title)}
          />
        </div>
        <div className="dialog-title">
          {translate(resources.employeeCards.buy.step4.dialog.title)}
        </div>
        <div className="dialog-text centered">
          <div>
            {translate(
              resources.employeeCards.buy.step4.dialog.successfulTransaction,
            )}
          </div>
          <div>
            {cardFormFactor === CardFormFactor.Plastic
              ? translate(
                  resources.employeeCards.buy.step4.dialog.physicalMessage,
                )
              : translate(
                  resources.employeeCards.buy.step4.dialog.virtualMessage,
                )}
          </div>
        </div>
      </div>
      <div className="dialog-actions vertical">
        <Button onClick={onClose} className="btn btn-outline">
          {translate(resources.understood)}
        </Button>
      </div>
    </Dialog>
  );
};
